import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, Menu, X, ChevronDown, User as UserIcon, Package, LogOut } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { categories } from '../data/categories';
export function Header() {
  const {
    itemCount,
    dispatch
  } = useCart();
  const {
    user,
    isAuthenticated,
    logout
  } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Close user dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserOpen(false);
      }
    };
    if (isUserOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isUserOpen]);
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const query = formData.get('q');
    if (query) {
      navigate(`/shop?q=${query}`);
      setIsMobileMenuOpen(false);
    }
  };
  const handleLogout = () => {
    logout();
    setIsUserOpen(false);
    navigate('/');
  };
  return <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200" data-id="element-146">
      {/* Announcement Bar */}
      <div className="bg-gray-900 text-white text-xs py-2 text-center px-4" data-id="element-147">
        Free shipping on orders over MVR 500 &bull; Pay securely with Bank of
        Maldives
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-148">
        <div className="flex justify-between items-center h-16" data-id="element-149">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center" data-id="element-150">
            <Link to="/" className="flex items-center gap-2" data-id="element-151">
              <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center text-red-600 font-bold text-xl" data-id="element-152">
                B
              </div>
              <span className="font-bold text-xl text-gray-900 tracking-tight" data-id="element-153">
                Beach Shop
              </span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8" data-id="element-154">
            <Link to="/shop" className="text-gray-600 hover:text-gray-900 font-medium text-sm" data-id="element-155">
              Shop All
            </Link>

            <div className="relative group" onMouseEnter={() => setIsCategoriesOpen(true)} onMouseLeave={() => setIsCategoriesOpen(false)} data-id="element-156">
              <button type="button" className="flex items-center text-gray-600 hover:text-gray-900 font-medium text-sm gap-1" data-id="element-157">
                Categories <ChevronDown className="w-4 h-4" data-id="element-158" />
              </button>

              {isCategoriesOpen && <div className="absolute top-full left-0 w-48 pt-2" data-id="element-159">
                  <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2" data-id="element-160">
                    {categories.map(cat => <Link key={cat.id} to={`/shop/${cat.id}`} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600" onClick={() => setIsCategoriesOpen(false)} data-id="element-161">
                        {cat.name}
                      </Link>)}
                  </div>
                </div>}
            </div>

            <Link to="/about" className="text-gray-600 hover:text-gray-900 font-medium text-sm" data-id="element-162">
              About
            </Link>
            <Link to="/contact" className="text-gray-600 hover:text-gray-900 font-medium text-sm" data-id="element-163">
              Contact
            </Link>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-3 sm:gap-4" data-id="element-164">
            <form onSubmit={handleSearch} className="hidden lg:flex relative" data-id="element-165">
              <input type="text" name="q" placeholder="Search products..." className="pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-48 transition-all focus:w-64" data-id="element-166" />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-167" />
            </form>

            <button type="button" className="lg:hidden text-gray-600 hover:text-gray-900" data-id="element-168">
              <Search className="w-5 h-5" data-id="element-169" />
            </button>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef} data-id="element-170">
              {isAuthenticated ? <button type="button" onClick={() => setIsUserOpen(!isUserOpen)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 p-1" aria-label="Open account menu" data-id="element-171">
                  <div className="w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold" data-id="element-172">
                    {user?.firstName?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 hidden sm:block" data-id="element-173" />
                </button> : <Link to="/login" className="text-gray-600 hover:text-gray-900 p-1 flex items-center" aria-label="Sign in" data-id="element-174">
                  <UserIcon className="w-6 h-6" data-id="element-175" />
                </Link>}

              {isAuthenticated && isUserOpen && <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50" data-id="element-176">
                  <div className="px-4 py-2 border-b border-gray-100 mb-1" data-id="element-177">
                    <div className="text-sm font-semibold text-gray-900 truncate" data-id="element-178">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-xs text-gray-500 truncate" data-id="element-179">
                      {user?.email}
                    </div>
                  </div>
                  <Link to="/account" onClick={() => setIsUserOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-180">
                    <UserIcon className="w-4 h-4 text-gray-400" data-id="element-181" /> My Profile
                  </Link>
                  <Link to="/account/orders" onClick={() => setIsUserOpen(false)} className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-182">
                    <Package className="w-4 h-4 text-gray-400" data-id="element-183" /> My Orders
                  </Link>
                  <button type="button" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-100 mt-1 pt-3" data-id="element-184">
                    <LogOut className="w-4 h-4 text-gray-400" data-id="element-185" /> Sign Out
                  </button>
                </div>}
            </div>

            <button type="button" className="relative text-gray-600 hover:text-gray-900 p-1" onClick={() => dispatch({
            type: 'TOGGLE_CART',
            payload: true
          })} aria-label="Open cart" data-id="element-186">
              <ShoppingBag className="w-6 h-6" data-id="element-187" />
              {itemCount > 0 && <span className="absolute top-0 right-0 -mt-1 -mr-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white" data-id="element-188">
                  {itemCount}
                </span>}
            </button>

            <button type="button" className="md:hidden text-gray-600 hover:text-gray-900 ml-1" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-189">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-190" /> : <Menu className="w-6 h-6" data-id="element-191" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <div className="md:hidden border-t border-gray-200 bg-white px-4 pt-2 pb-4 space-y-1" data-id="element-192">
          <form onSubmit={handleSearch} className="mb-4 relative mt-2" data-id="element-193">
            <input type="text" name="q" placeholder="Search products..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" data-id="element-194" />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-195" />
          </form>

          <Link to="/shop" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-196">
            Shop All
          </Link>

          <div className="px-3 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-197">
            Categories
          </div>
          {categories.map(cat => <Link key={cat.id} to={`/shop/${cat.id}`} className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900" onClick={() => setIsMobileMenuOpen(false)} data-id="element-198">
              {cat.name}
            </Link>)}

          <Link to="/about" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50 mt-2" onClick={() => setIsMobileMenuOpen(false)} data-id="element-199">
            About
          </Link>
          <Link to="/contact" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-200">
            Contact
          </Link>

          <div className="border-t border-gray-100 my-2" data-id="element-201" />

          {isAuthenticated ? <>
              <Link to="/account" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-202">
                My Profile
              </Link>
              <Link to="/account/orders" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-203">
                My Orders
              </Link>
              <button type="button" onClick={() => {
          handleLogout();
          setIsMobileMenuOpen(false);
        }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" data-id="element-204">
                Sign Out
              </button>
            </> : <>
              <Link to="/login" className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-205">
                Sign In
              </Link>
              <Link to="/register" className="block px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-gray-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-206">
                Create Account
              </Link>
            </>}
        </div>}
    </header>;
}